/**
 * EPIC 47 — admin-web client for the unified in-app messaging core.
 *
 * All mutations go through Cloud Functions (postMessage / markConversationRead —
 * /conversations is callable-write-only per firestore.rules). Reads are live
 * Firestore subscriptions gated by the read rule: a non-staff participant sees
 * their own thread; staff see all. Queries use a single `where` and sort
 * client-side so no composite index is required.
 */

import type {
  Conversation,
  ConversationRead,
  Message,
  MessageAttachment,
} from '@rendimees24/shared-types';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage';

import { httpsCallableWithLog as httpsCallable } from './errorLog';
import { getFirebase } from './firebase';

/** Deterministic id for a worker's single dispatch thread (mirrors the callable). */
export function workerDispatchConversationId(workerUid: string): string {
  return `worker_${workerUid}`;
}

/** Deterministic id for an order thread (mirrors the callable) — one per job. */
export function orderConversationId(jobId: string): string {
  return `order_${jobId}`;
}

/**
 * UX 7.3 (Q30) — deterministic id of a multi-trade order's ONE thread
 * (mirrors the callable). Every Job of the submission shares `orderGroupId`.
 */
export function orderGroupConversationId(orderGroupId: string): string {
  return `ordergroup_${orderGroupId}`;
}

/** The thread an order's messages live in: its group's, else its own. */
export function orderThreadId(job: { id: string; orderGroupId?: string | null }): string {
  return job.orderGroupId ? orderGroupConversationId(job.orderGroupId) : orderConversationId(job.id);
}

/** Deterministic id for a site thread (mirrors the callable) — one per object. */
export function siteConversationId(siteId: string): string {
  return `site_${siteId}`;
}

// ── Attachment constraints (mirror functions/messaging/lib/conversations.ts) ──
export const MAX_MESSAGE_ATTACHMENTS = 5;
export const MAX_MESSAGE_ATTACHMENT_BYTES = 15 * 1024 * 1024;
/** `accept` attribute for the file picker — images + PDF. */
export const MESSAGE_ATTACHMENT_ACCEPT = 'image/*,application/pdf';

export function isAllowedMessageAttachmentType(contentType: string): boolean {
  return /^image\/[\w.+-]+$/.test(contentType) || contentType === 'application/pdf';
}

export interface PostMessageInput {
  /** Post to an existing thread… */
  conversationId?: string;
  /** …or resolve/create a thread by scope. */
  type?: 'worker_dispatch' | 'order' | 'site' | 'client_dispatch';
  scope?: { workerId?: string; jobId?: string; siteId?: string; clientId?: string };
  /** May be empty when the message is attachment-only. */
  text: string;
  /** Validated storage PATHS (never tokenized URLs) — resolved at render. */
  attachments?: MessageAttachment[];
  /** UX 7.4 — one id per written message, so a replay never posts it twice. */
  clientMessageId?: string;
}

/**
 * Upload one message attachment direct-to-Storage under
 * `conversations/{conversationId}/{uploadId}/{safeName}` and return the stored
 * metadata (PATH, not a URL). The postMessage callable re-validates the path
 * prefix, type and size, so this is the convenience half of a
 * defence-in-depth pair. `onProgress` reports 0–1 for the progress chip.
 */
export async function uploadMessageAttachment(
  conversationId: string,
  file: File,
  onProgress?: (fraction: number) => void,
): Promise<MessageAttachment> {
  const { storage } = getFirebase();
  // Unguessable upload id — hardens the path-secrecy layer (the real read
  // boundary is the participant-scoped message-doc Firestore rule).
  const uploadId = crypto.randomUUID();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_') || 'file';
  const storagePath = `conversations/${conversationId}/${uploadId}/${safeName}`;
  const contentType = file.type || 'application/octet-stream';
  const task = uploadBytesResumable(storageRef(storage, storagePath), file, { contentType });
  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      reject,
      () => resolve(),
    );
  });
  return { storagePath, name: file.name, contentType, sizeBytes: file.size };
}

/** Resolve an attachment storagePath to a download URL for rendering. */
export async function messageAttachmentUrl(storagePath?: string): Promise<string | undefined> {
  if (!storagePath) return undefined;
  const { storage } = getFirebase();
  try {
    return await getDownloadURL(storageRef(storage, storagePath));
  } catch {
    return undefined;
  }
}

export async function postMessage(
  input: PostMessageInput,
): Promise<{ ok: true; conversationId: string; messageId: string }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<
    PostMessageInput,
    { ok: true; conversationId: string; messageId: string }
  >(functions, 'postMessage');
  return (await callable(input)).data;
}

export async function markConversationRead(conversationId: string): Promise<void> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ conversationId: string }, { ok: true }>(
    functions,
    'markConversationRead',
  );
  await callable({ conversationId });
}

/**
 * Settle notifications the reader has seen.
 *
 * Chat notifications also clear via `markConversationRead` when the thread is
 * opened; this is the path for everything else, which has no conversation to
 * open. Fire-and-forget at the call site — a badge that clears a moment late is
 * not worth blocking a navigation for.
 */
export async function markNotificationsRead(
  input: { notificationIds: string[] } | { all: true },
): Promise<{ marked: number }> {
  const { functions } = getFirebase();
  const callable = httpsCallable<typeof input, { ok: true; marked: number }>(
    functions,
    'markNotificationsRead',
  );
  const { data } = await callable(input);
  return data;
}

/**
 * UX 7.1 — this person's own read marks, `users/{uid}/conversationReads`
 * (owner-readable, written only by markConversationRead). Map of
 * conversationId → lastReadAt (ISO). A refused/failed read yields an empty
 * map: every thread then falls back to the shared counter.
 */
export function subscribeMyConversationReads(
  uid: string,
  onReads: (reads: Map<string, string>) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    collection(firestore, 'users', uid, 'conversationReads'),
    (snap) => {
      const out = new Map<string, string>();
      for (const d of snap.docs) {
        const r = d.data() as Partial<ConversationRead>;
        if (typeof r.lastReadAt === 'string') out.set(d.id, r.lastReadAt);
      }
      onReads(out);
    },
    () => onReads(new Map()),
  );
}

/** Live single conversation doc; null while it does not yet exist / is unreadable. */
export function subscribeConversation(
  conversationId: string,
  onConv: (c: Conversation | null) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    doc(firestore, 'conversations', conversationId),
    (snap) =>
      onConv(
        snap.exists() ? ({ id: snap.id, ...(snap.data() as DocumentData) } as Conversation) : null,
      ),
    () => onConv(null),
  );
}

/** Live message stream for a thread, oldest first. */
export function subscribeMessages(
  conversationId: string,
  onMsgs: (m: Message[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    query(collection(firestore, 'conversations', conversationId, 'messages'), orderBy('at', 'asc')),
    (snap) =>
      onMsgs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Message)),
    (error) => (onError ? onError(error) : onMsgs([])),
  );
}

function byUpdatedAtDesc(a: Conversation, b: Conversation): number {
  return millis(b.updatedAt) - millis(a.updatedAt);
}

function millis(v: unknown): number {
  if (
    v &&
    typeof v === 'object' &&
    'toMillis' in v &&
    typeof (v as { toMillis: () => number }).toMillis === 'function'
  ) {
    return (v as { toMillis: () => number }).toMillis();
  }
  if (typeof v === 'string') return Date.parse(v) || 0;
  return 0;
}

/**
 * A single non-staff user's OWN conversations — every thread they participate in
 * (uid ∈ participantUids), most-recently-active first. Used by the app-bar
 * MessagesMenu for the worker viewpoint. A SINGLE `array-contains` where + a
 * client-side sort, so NO composite index is required, and the read is covered by
 * the existing participant read rule (uid ∈ participantUids). In v1 a worker has
 * exactly one thread (`worker_{uid}`); the query is future-proof for more.
 */
export function subscribeMyConversations(
  uid: string,
  onConvs: (c: Conversation[]) => void,
): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    query(collection(firestore, 'conversations'), where('participantUids', 'array-contains', uid)),
    (snap) => {
      onConvs(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Conversation)
          .sort(byUpdatedAtDesc),
      );
    },
    () => onConvs([]),
  );
}

/**
 * The CLIENT company's inbox — every thread belonging to the companies this
 * user has access to, most-recently-active first.
 *
 * Queried by the flat `clientId`, NOT by participation: order / site /
 * client_dispatch threads deliberately leave `participantUids` empty because
 * the client side is a mutable team, and a static member list would go stale
 * the moment someone joins or leaves. The Firestore rule authorises exactly the
 * same condition (`scope.clientId in permissions`), so every document this
 * query can return is one the rule already permits — which is what makes the
 * query legal rather than rejected wholesale.
 *
 * `in` accepts at most 30 values; a user with more client companies than that
 * would need chunking, which no account is close to today.
 */
/**
 * The conversation types a client_user is allowed to read. Must stay in sync
 * with the client clause of `conversationReadable` in firestore.rules — the
 * query has to name them for the list to be authorised at all.
 */
const CLIENT_READABLE_TYPES = ['order', 'site', 'client_dispatch'] as const;

export function subscribeClientConversations(
  clientIds: readonly string[],
  onConvs: (c: Conversation[]) => void,
  /**
   * PB-13 — the objects to read one by one when the company-wide list is
   * refused (restricted mode or an excluded object: the rule cannot prove an
   * unfiltered list safe). Resolved by the server, which knows which objects
   * this person may see. Omitted → a refusal shows an empty inbox, as before.
   */
  visibleSiteIds?: (clientId: string) => Promise<string[]>,
): () => void {
  if (clientIds.length === 0) {
    // Firestore rejects an empty `in`, and there is nothing to show anyway.
    onConvs([]);
    return () => {};
  }
  const { firestore } = getFirebase();
  let narrowed: (() => void) | null = null;
  let narrowedKey: string | null = null;
  let stopRefresh: (() => void) | null = null;
  let closed = false;
  /**
   * (Re)read which objects I may see and re-listen only when that set changed.
   * Rights change while the page is open — an object assigned to me, a
   * delegation, an exclusion lifted — and a one-off read left those threads
   * missing until a reload (review of 373b3b9).
   */
  const narrowTo = async (clientId: string) => {
    const siteIds = [...new Set(await visibleSiteIds!(clientId))].sort();
    const key = siteIds.join(',');
    if (closed || key === narrowedKey) return;
    narrowed?.();
    narrowedKey = key;
    narrowed = subscribeNarrowClientInbox(clientId, siteIds, onConvs);
  };
  const unsubWide = onSnapshot(
    query(
      collection(firestore, 'conversations'),
      // BOTH constraints are load-bearing, and each was verified against the
      // live rules with a real client_user token:
      //
      //   • the FLAT `clientId` — filtering the nested `scope.clientId` is
      //     DENIED even with everything else identical, because the rule's
      //     authorisation clause cannot be matched to a nested path;
      //   • the `type` restriction — without it the query is DENIED too. Rules
      //     are not filters: a list is permitted only when the query provably
      //     returns documents the rule allows, and the rule admits a
      //     client_user to exactly these three types. Unconstrained, the query
      //     could in principle match a worker_dispatch thread, so Firestore
      //     refuses the whole thing.
      //
      // The failure is silent in the worst way — a single-document read of the
      // very same conversation succeeds, so the data looks reachable.
      where('clientId', 'in', clientIds.slice(0, 30)),
      where('type', 'in', CLIENT_READABLE_TYPES),
    ),
    (snap) => {
      onConvs(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Conversation)
          .sort(byUpdatedAtDesc),
      );
    },
    () => {
      onConvs([]);
      // The portal is one company (useActiveClientIds); a narrowed inbox is
      // per company, so only that case falls back.
      if (!visibleSiteIds || clientIds.length !== 1) return;
      const clientId = clientIds[0];
      const refresh = () => {
        narrowTo(clientId).catch(() => {
          /* keep what is shown — the next refresh tries again */
        });
      };
      refresh();
      stopRefresh = watchRightsChanges(refresh);
    },
  );
  return () => {
    closed = true;
    unsubWide();
    stopRefresh?.();
    narrowed?.();
  };
}

/** How often a narrowed inbox re-reads its visible objects while open. */
export const NARROW_INBOX_REFRESH_MS = 2 * 60_000;

/** Call `refresh` when the tab regains focus and on a slow interval. */
function watchRightsChanges(refresh: () => void): () => void {
  const timer = setInterval(refresh, NARROW_INBOX_REFRESH_MS);
  const onFocus = () => refresh();
  window.addEventListener('focus', onFocus);
  return () => {
    clearInterval(timer);
    window.removeEventListener('focus', onFocus);
  };
}

/**
 * The inbox of a member whose company narrowed visibility: the company channel,
 * then each visible object's site and order threads. Every query pins the
 * object, so the rule can check it — `isSiteResponsible` reads the NESTED
 * `scope.clientId` on a site thread, hence both client ids are constrained.
 * Equality filters only, so no composite index is needed. An order without an
 * object is not reached here (residual, noted in PB-13).
 */
function subscribeNarrowClientInbox(
  clientId: string,
  siteIds: readonly string[],
  onConvs: (c: Conversation[]) => void,
): () => void {
  const { firestore } = getFirebase();
  const parts = new Map<string, Conversation[]>();
  const emit = () => {
    const byId = new Map<string, Conversation>();
    for (const list of parts.values()) for (const c of list) byId.set(c.id, c);
    onConvs([...byId.values()].sort(byUpdatedAtDesc));
  };
  const listen = (key: string, q: ReturnType<typeof query>) =>
    onSnapshot(
      q,
      (snap) => {
        parts.set(
          key,
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Conversation),
        );
        emit();
      },
      () => {
        parts.delete(key);
        emit();
      },
    );
  const unsubs = [
    listen(
      'company',
      query(
        collection(firestore, 'conversations'),
        where('clientId', '==', clientId),
        where('type', '==', 'client_dispatch'),
      ),
    ),
    ...[...new Set(siteIds)].map((siteId) =>
      listen(
        `site:${siteId}`,
        query(
          collection(firestore, 'conversations'),
          where('clientId', '==', clientId),
          where('scope.clientId', '==', clientId),
          where('scope.siteId', '==', siteId),
          where('type', 'in', ['site', 'order']),
        ),
      ),
    ),
  ];
  return () => unsubs.forEach((u) => u());
}

/**
 * Unified admin inbox (P1b) — EVERY conversation type (worker_dispatch, order,
 * site), most-recently-active first. Staff read all via the staff-role rule; no
 * `where` filter so no composite index is needed (sorted client-side).
 */
export function subscribeAllConversations(onConvs: (c: Conversation[]) => void): () => void {
  const { firestore } = getFirebase();
  return onSnapshot(
    collection(firestore, 'conversations'),
    (snap) => {
      onConvs(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as DocumentData) }) as Conversation)
          // UX 7.3 — a thread folded into its order group's thread is history.
          .filter((c) => !c.mergedInto)
          .sort(byUpdatedAtDesc),
      );
    },
    () => onConvs([]),
  );
}

/**
 * ADMIN-ONLY one-shot: fold the legacy per-order (jobs) and per-site (sites)
 * message threads into /conversations. `dryRun` reports scanned/migrated/skipped
 * without writing.
 */
export interface BackfillMessageThreadsResult {
  ok: true;
  dryRun: boolean;
  scanned: number;
  migrated: number;
  skipped: number;
  conversationsTouched: number;
  /** UX 7.3 — per-job order threads folded into their order group's thread. */
  groupThreadsFolded?: number;
  groupMessagesCopied?: number;
  groupThreadsCreated?: number;
}

export async function backfillMessageThreads(
  input: { dryRun?: boolean } = {},
): Promise<BackfillMessageThreadsResult> {
  const { functions } = getFirebase();
  const callable = httpsCallable<{ dryRun?: boolean }, BackfillMessageThreadsResult>(
    functions,
    'backfillMessageThreads',
  );
  return (await callable(input)).data;
}
